<svelte:options runes={true} />

<script lang="ts">
  import IconFiltersLines from "~icons/dusk/filters-lines";
  import IconChevronDown from "~icons/dusk/chevron-down";
  import { Accordion } from "bits-ui";
  import type { Filter, FilterState } from "~/lib/filters/types";
  import { applyFilterChange, getActiveCount } from "~/lib/filters/utils";
  import Button from "~/components/ui/Button/Button.svelte";
  import FilterAccordionItem from "~/components/filters/FilterAccordionItem/FilterAccordionItem.svelte";
  import SlideOverDialog from "~/components/ui/SlideOverDialog/SlideOverDialog.svelte";
  import SlideOverDialogHeader from "~/components/ui/SlideOverDialog/SlideOverDialogHeader.svelte";
  import SlideOverDialogFooter from "~/components/ui/SlideOverDialog/SlideOverDialogFooter.svelte";

  interface Props {
    filters: Filter[];
    activeFilters?: FilterState;
    onChange?: (activeFilters: FilterState) => void;
    onReset?: () => void;
    class?: string;
  }

  let {
    filters,
    activeFilters = $bindable({}),
    onChange,
    onReset,
    class: className = "",
  }: Props = $props();

  let isOpen = $state(false);
  let openSections: string[] = $state([]);

  function openPanel() {
    openSections = filters.filter((f) => getActiveCount(f, activeFilters) > 0).map((f) => f.id);
    isOpen = true;
  }

  function closePanel() {
    isOpen = false;
  }

  function handleReset() {
    activeFilters = {};
    onChange?.({});
    onReset?.();
    closePanel();
  }

  function handleFilterChange(id: string, value: string[] | [number, number]) {
    activeFilters = applyFilterChange(
      filters.find((f) => f.id === id),
      activeFilters,
      id,
      value,
    );
    onChange?.(activeFilters);
  }
</script>

<button type="button" class="da-mobile-filter-trigger {className}" onclick={openPanel}>
  <IconFiltersLines aria-hidden="true" width="16" height="16" />
  <span class="da-mobile-filter-trigger__label">Filter</span>
  <IconChevronDown aria-hidden="true" width="16" height="16" />
</button>

<SlideOverDialog bind:open={isOpen} side="left">
  {#snippet header()}
    <SlideOverDialogHeader onClose={closePanel}>
      <span class="da-mobile-filter-panel__title">Filters</span>
    </SlideOverDialogHeader>
  {/snippet}

  <div class="da-mobile-filter-panel__body">
    <Accordion.Root
      type="multiple"
      class="da-mobile-filter-panel__accordion"
      bind:value={openSections}
    >
      {#each filters as filter (filter.id)}
        <FilterAccordionItem
          {filter}
          value={activeFilters[filter.id]}
          onChange={handleFilterChange}
          activeCount={getActiveCount(filter, activeFilters)}
        />
      {/each}
    </Accordion.Root>
  </div>

  {#snippet footer()}
    <SlideOverDialogFooter>
      <Button
        variant="secondary"
        size="lg"
        class="da-mobile-filter-panel__action"
        onclick={handleReset}
      >
        Reset
      </Button>
      <Button
        variant="primary"
        size="lg"
        class="da-mobile-filter-panel__action"
        onclick={closePanel}
      >
        Apply
      </Button>
    </SlideOverDialogFooter>
  {/snippet}
</SlideOverDialog>

<style>
  @layer aurora {
    .da-mobile-filter-trigger {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: var(--da-space-px-6);
      flex: 1 1 auto;
      padding-inline: var(--da-space-3);
      padding-block: var(--da-space-2);
      background: var(--da-color-brand-sand-100);
      border: 1px solid var(--da-color-brand-sand-900);
      border-radius: var(--da-radius-lg);
      box-shadow: var(--da-shadow-xs);
      font-size: var(--da-font-size-xxs);
      font-weight: var(--da-font-weight-medium);
      color: var(--da-color-charcoal-500);
      cursor: pointer;
      width: 100%;
    }

    .da-mobile-filter-trigger__label {
      flex: 1;
      text-align: left;
      font-family: var(--da-font-family-primary);
    }

    .da-mobile-filter-bar__results {
      margin: 0;
      width: 100%;
      text-align: center;
      padding-block: var(--da-space-2);
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-sm);
      font-weight: var(--da-font-weight-medium);
      color: var(--da-color-charcoal-300);
    }

    .da-mobile-filter-panel__title {
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-md);
      font-weight: var(--da-font-weight-bold);
      color: var(--da-color-charcoal-500);
      letter-spacing: var(--da-letter-spacing-display);
      text-transform: uppercase;
    }

    .da-mobile-filter-panel__body {
      flex: 1;
      padding: 0 var(--da-space-4);
    }

    :global(.da-mobile-filter-panel__action) {
      flex: 1;
    }

    :global(.da-mobile-filter-panel__accordion) {
      display: flex;
      flex-direction: column;
    }
  }
</style>
